import { useMemo } from "react";
import invariant from "tiny-invariant";
import { ArrayFilterComparator } from "@/react/components/shared/Filter/FilterTypes/ArrayFilterComparator/ArrayFilterComparator";
import type { ArrayComparatorParam } from "@/react/components/shared/Filter/FilterTypes/ArrayFilterComparator/arrayComparators";
import { isArrayComparatorParam } from "@/react/components/shared/Filter/FilterTypes/ArrayFilterComparator/arrayComparators";
import { useArrayComparatorSelectedItem } from "@/react/components/shared/Filter/FilterTypes/ArrayFilterComparator/useArrayComparatorSelectedItem";
import type { BaseFilter } from "@/react/components/shared/MemberFilters/types";
import { Filter } from "@circle-react-shared/FilterV3";
import { FilterChip } from "../../shared/FilterV3/FilterChipV2";
import { useFilterProvider } from "../FilterProvider";
import type { ArrayFilterConfiguration } from "../types";

interface ArrayFilterInputProps {
  configuration: ArrayFilterConfiguration;
}

// This component is not used in the FilterKit yet so implementation might not be correct
export const ArrayFilterInput = ({ configuration }: ArrayFilterInputProps) => {
  const { setFilter, filterParamForKey, clearFilterByKey } =
    useFilterProvider();

  const param = useMemo((): ArrayComparatorParam => {
    const existingFilter = filterParamForKey(configuration.key) as
      | ArrayComparatorParam
      | undefined;

    return {
      id: configuration.key,
      key: configuration.key,
      filter_type: existingFilter?.filter_type ?? "is",
      value: existingFilter?.value,
    };
  }, [filterParamForKey, configuration.key]);

  const options = configuration.payload.options.map(opt => ({
    label: opt.text,
    value: String(opt.value),
  }));

  const renderSelectedValue = useArrayComparatorSelectedItem(options);

  const handleApply = (value: BaseFilter | null) => {
    if (value === null) {
      clearFilterByKey(configuration.key);
      return;
    }

    invariant(isArrayComparatorParam(value), "Invalid array comparator param");
    setFilter(configuration.key, {
      ...value,
      search_key: configuration.search_key,
    });
  };

  return (
    <Filter onApply={handleApply}>
      <FilterChip renderSelectedValue={() => renderSelectedValue(param)}>
        {configuration.label}
      </FilterChip>
      <Filter.Popover title={configuration.label}>
        <ArrayFilterComparator
          name={configuration.key}
          options={options}
          param={param}
        />
      </Filter.Popover>
    </Filter>
  );
};
